import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Loader, Message } from "semantic-ui-react";
import { bookingApi } from "../api/api";
import { mapBooking } from "../api/mappers";
import { useBooking } from "../context/BookingContext";
import { useAuth } from "../context/AuthContext";
import useBill from "../hooks/useBill";
import { hasCapability } from "../utils/capabilities";
import BillView, { BILLABLE_BOOKING_STATUSES } from "../components/admin/billing/BillView";

/* /gh-admin/bill/:bookingId — a booking's bill as a page of its own.
   Back returns to wherever the admin came from (normally the booking, which
   reloads its Bill card on the way back). */
export default function BillPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { bookings = [] } = useBooking();
  const can = (cap) => hasCapability(currentUser, cap);
  const fromList = useMemo(
    () => bookings.find((b) => String(b.id) === String(bookingId)) || null,
    [bookings, bookingId],
  );
  const [fetched, setFetched] = useState(null);
  const [fetchError, setFetchError] = useState("");
  const booking = fromList || fetched;
  const billState = useBill(Number(bookingId), { enabled: can("bill.view") });

  // Opened directly (a bookmark, a reload): the booking list may not hold it.
  useEffect(() => {
    if (fromList || fetched) return;
    let live = true;
    bookingApi.get(bookingId)
      .then((data) => live && setFetched(mapBooking(data)))
      .catch((e) => live && setFetchError(e.message || "Could not load the booking."));
    return () => { live = false; };
  }, [bookingId, fromList, fetched]);

  const onBack = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate(`/gh-admin?tab=bookings&b=${bookingId}`);
  };

  if (!can("bill.view")) {
    return <Container><Message warning content="You do not have access to bills." /></Container>;
  }
  if (fetchError) return <Container><Message negative content={fetchError} /></Container>;
  if (!booking) return <Loader active inline="centered" content="Loading…" />;

  return (
    <Container fluid style={{ padding: "0 16px 24px" }}>
      <BillView booking={booking} billState={billState} can={can}
        canMakeBill={BILLABLE_BOOKING_STATUSES.has(booking.status)} onBack={onBack} />
    </Container>
  );
}
